// projects.js — the Projects layer, pure and tested. A Watch_Projects row
// set in, a three-level tree out; colors, labels and the project filter
// predicate come from it. Watch.js renders; watchApi.js maps rows.
//
//   tree     { byId: Map id -> node, roots: node[] }
//   node     { id, name, parentId, color, sortOrder, status, type, policyRef,
//              insuredId, spId, depth (1..3), path: [name, …], children: node[] }
//
// The tree allows THREE levels (project → sub → sub-sub). buildProjectTree
// throws on a fourth, on a cycle, and on a parent that does not exist, so a
// bad list fails loudly at load rather than rendering half a tree. A node
// with no Color inherits its parent's; a root with none takes a palette
// slot by root order (the pre-Projects rule, kept so a hand-created project
// still gets a color before Marc picks one).

export const MAX_DEPTH = 3;
export const NO_PROJECT = '';                    // the key for cards with no ProjectId
export const NO_PROJECT_COLOR = '#4a5563';       // neutral slate; not a palette hue
export const NO_PROJECT_LABEL = '—';

// Same 14 hues Watch.js assigned per Project string before the Projects
// layer; still the fallback for a root row with no Color of its own.
export const PROJECT_PALETTE = [
  '#4ac0d0', '#9a6be0', '#2e9e44', '#e0704a', '#5a8de0', '#d05aa0', '#3fb98a',
  '#963a2a', '#c65fc6', '#8ab534', '#2f8fa8', '#a8748a', '#4f55b8', '#b09a55',
];

const HEX_RE = /^#[0-9a-f]{6}$/i;
const bySort = (a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0) || a.name.localeCompare(b.name) || a.id.localeCompare(b.id);

/**
 * @param projects  [{ id, name, parentId, color, sortOrder, … }] as watchApi maps them
 * @returns {{ byId: Map, roots: node[] }}
 */
export function buildProjectTree(projects) {
  const byId = new Map();
  for (const p of projects || []) {
    if (!p || !p.id) continue;
    if (byId.has(p.id)) throw new Error(`projects: duplicate ProjectId ${p.id}`);
    byId.set(p.id, { ...p, name: p.name || p.id, parentId: p.parentId || '', children: [], depth: 0, path: [] });
  }
  for (const n of byId.values()) {
    if (!n.parentId) continue;
    const parent = byId.get(n.parentId);
    if (!parent) throw new Error(`projects: ${n.id} names a parent ${n.parentId} that does not exist`);
    parent.children.push(n);
  }
  const roots = [...byId.values()].filter(n => !n.parentId).sort(bySort);
  // Depth, path and inherited color, top-down. A node never reached from a
  // root sits on a cycle.
  const seen = new Set();
  const walk = (n, depth, path, color) => {
    if (depth > MAX_DEPTH) throw new Error(`projects: ${n.id} sits at depth ${depth}; the tree allows ${MAX_DEPTH}`);
    if (seen.has(n.id)) throw new Error(`projects: cycle through ${n.id}`);
    seen.add(n.id);
    n.depth = depth;
    n.path = [...path, n.name];
    n.color = HEX_RE.test(n.color || '') ? n.color : color;
    n.children.sort(bySort);
    for (const c of n.children) walk(c, depth + 1, n.path, n.color);
  };
  roots.forEach((r, i) => walk(r, 1, [], HEX_RE.test(r.color || '') ? r.color : PROJECT_PALETTE[i % PROJECT_PALETTE.length]));
  if (seen.size !== byId.size) {
    const stray = [...byId.keys()].find(id => !seen.has(id));
    throw new Error(`projects: cycle through ${stray}`);
  }
  return { byId, roots };
}

export const emptyTree = () => ({ byId: new Map(), roots: [] });

/** id -> hex for every node, plus the neutral entry for NO_PROJECT. */
export function projectColors(tree) {
  const out = { [NO_PROJECT]: NO_PROJECT_COLOR };
  for (const n of tree?.byId?.values?.() || []) out[n.id] = n.color;
  return out;
}

/** The band caption: "GREEN SPAN › 9/29 RENEWAL". Unknown or blank id → '—'. */
export function projectLabel(tree, id, sep = ' › ') {
  const n = id ? tree?.byId?.get(id) : null;
  return n ? n.path.join(sep) : NO_PROJECT_LABEL;
}
export function projectName(tree, id) {
  const n = id ? tree?.byId?.get(id) : null;
  return n ? n.name : NO_PROJECT_LABEL;
}
export const projectColorOf = (tree, id) => (id && tree?.byId?.get(id)?.color) || NO_PROJECT_COLOR;

/** Every node in display order: root, its subtree depth-first, next root. */
export function flattenTree(tree) {
  const out = [];
  const walk = (n) => { out.push(n); n.children.forEach(walk); };
  (tree?.roots || []).forEach(walk);
  return out;
}

/** The ids of a node and every descendant; [] for an unknown id. */
export function subtreeIds(tree, id) {
  const n = tree?.byId?.get(id);
  if (!n) return [];
  const out = [];
  const walk = (x) => { out.push(x.id); x.children.forEach(walk); };
  walk(n);
  return out;
}

/**
 * The PROJECT chip predicate: a card passes when its projectId is one of
 * the selected ids OR sits under one of them. The blank chip (NO_PROJECT)
 * matches cards with no projectId. An empty selection passes everything.
 */
export function matchesProjectFilter(item, selectedIds, tree) {
  if (!selectedIds || selectedIds.length === 0) return true;
  const pid = item?.projectId || NO_PROJECT;
  for (const sel of selectedIds) {
    if (sel === NO_PROJECT) { if (pid === NO_PROJECT) return true; continue; }
    if (pid === sel) return true;
    if (subtreeIds(tree, sel).includes(pid)) return true;
  }
  return false;
}

/** The chip values in tree order, only for projects some card actually carries; blank last when any card is unassigned. */
export function projectFilterValues(items, tree) {
  const present = new Set((items || []).map(i => i?.projectId || NO_PROJECT));
  const out = flattenTree(tree).map(n => n.id).filter(id => present.has(id));
  if (present.has(NO_PROJECT)) out.push(NO_PROJECT);
  return out;
}

/**
 * Cards grouped for the Projects view. Feed it EVERY item — not the board's
 * collapsed list — so a same-court child folded under its parent on the
 * board still counts and renders here.
 * @returns Map projectId -> { open: item[], fresh: item[], done: item[] },
 *   with NO_PROJECT holding the unassigned cards and every tree node present
 *   (possibly empty).
 *   `open`  = triaged, not Closed/Killed (parked Holding cards included)
 *   `fresh` = untriaged (the NEW rail's) — shown and counted, but its court
 *             is the sweep's guess, so it stays out of the court split
 *   `done`  = Closed or Killed
 */
export function groupByProject(items, tree, { isDone, isNew } = {}) {
  const done = typeof isDone === 'function' ? isDone : (i) => i.status === 'Closed' || i.status === 'Killed';
  const fresh = typeof isNew === 'function' ? isNew : (i) => i.status === 'Open' && i.triaged === false;
  const groups = new Map();
  const bucket = () => ({ open: [], fresh: [], done: [] });
  for (const n of flattenTree(tree)) groups.set(n.id, bucket());
  groups.set(NO_PROJECT, bucket());
  for (const i of items || []) {
    if (!i) continue;
    const key = i.projectId && groups.has(i.projectId) ? i.projectId : NO_PROJECT;
    groups.get(key)[done(i) ? 'done' : fresh(i) ? 'fresh' : 'open'].push(i);
  }
  return groups;
}

/**
 * The band summary over a node and its whole subtree: `open` = every live
 * card (triaged + NEW; done cards excluded from every count but `done`),
 * `fresh` = the NEW ones, the court split over triaged cards only, and the
 * hottest tier any descendant sits in — 'NOW' over 'TODAY', else ''.
 * `tierOf(id)` is optional; without it `tier` is ''. Works for NO_PROJECT
 * too (no subtree, just its own group).
 */
export function subtreeCounts(tree, groups, id, tierOf) {
  const ids = id === NO_PROJECT ? [NO_PROJECT] : subtreeIds(tree, id);
  const out = { open: 0, fresh: 0, done: 0, mine: 0, theirs: 0, holding: 0, tier: '' };
  for (const sid of ids) {
    const g = groups.get(sid);
    if (!g) continue;
    out.done += g.done.length;
    out.fresh += (g.fresh || []).length;
    out.open += (g.fresh || []).length;
    for (const i of g.open) {
      out.open++;
      if (i.court === 'Mine') out.mine++;
      else if (i.court === 'Theirs') out.theirs++;
      else if (i.court === 'Holding') out.holding++;
      const t = typeof tierOf === 'function' ? tierOf(i.id) : '';
      if (t === 'NOW') out.tier = 'NOW';
      else if (t === 'TODAY' && out.tier !== 'NOW') out.tier = 'TODAY';
    }
  }
  return out;
}

// ── search ──
export const SEARCH_MAX = 8;
const SCORE = { id: 100, titleStart: 80, title: 60, project: 50, who: 40, insured: 40, text: 20 };
const lc = (v) => String(v || '').toLowerCase();
/**
 * The PROJECTS search: the top SEARCH_MAX cards for `q`, scored per field.
 *   #N or an all-digit q equal to the WatchId   100
 *   title starts with / contains                 80 / 60
 *   project label (the path) contains            50
 *   counterparty, its email, source who/org,
 *   insured contains                             40
 *   synopsis standing/move, intake summary,
 *   next action contains                         20
 * Several words are AND-ed: every term must hit somewhere, the best score
 * per term is summed. Ties: Open before Closed/Killed, then the lower id.
 * `why` names the field of the single best hit.
 * @returns [{ item, score, why }]
 */
export function searchCards(items, q, { labelOf = () => '', max = SEARCH_MAX } = {}) {
  const terms = lc(q).trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  const out = [];
  for (const item of items || []) {
    if (!item) continue;
    const idStr = String(item.id ?? '');
    const title = lc(item.title);
    const fields = [
      ['project', lc(labelOf(item))],
      ['who', [item.counterparty, item.counterpartyEmail, ...(item.sources || []).map(s => `${s.who || ''} ${s.org || ''}`)].map(lc).join(' ')],
      ['insured', lc(item.insuredName)],
      ['text', [item.synopsis?.standing, item.synopsis?.move, item.intake?.summary, item.nextAction].map(lc).join(' ')],
    ];
    let score = 0, why = '', best = 0;
    for (const term of terms) {
      let s = 0, w = '';
      const num = term.startsWith('#') ? term.slice(1) : term;
      if (/^\d+$/.test(num) && num === idStr) { s = SCORE.id; w = 'id'; }
      else if (title.startsWith(term)) { s = SCORE.titleStart; w = 'title'; }
      else if (title.includes(term)) { s = SCORE.title; w = 'title'; }
      else {
        for (const [name, hay] of fields) { if (hay.includes(term) && SCORE[name] > s) { s = SCORE[name]; w = name; } }
      }
      if (!s) { score = 0; break; }                       // AND: a term with no hit drops the card
      score += s;
      if (s > best) { best = s; why = w; }
    }
    if (score) out.push({ item, score, why });
  }
  const done = (i) => (i.status === 'Closed' || i.status === 'Killed' ? 1 : 0);
  out.sort((a, b) => b.score - a.score || done(a.item) - done(b.item) || (a.item.id ?? 0) - (b.item.id ?? 0));
  return out.slice(0, max);
}

/** The next PRJ-NNNNN after the ids in `tree` (the PersonId pattern). */
export function nextProjectId(tree) {
  let max = 0;
  for (const id of tree?.byId?.keys?.() || []) { const m = /^PRJ-(\d+)$/.exec(id); if (m) max = Math.max(max, Number(m[1])); }
  return `PRJ-${String(max + 1).padStart(5, '0')}`;
}

/** Whether a node may take a child: depth 1 or 2 only (MAX_DEPTH 3). Blank parentId = a new root, always allowed. */
export function canAddChild(tree, parentId) {
  if (!parentId) return true;
  const n = tree?.byId?.get(parentId);
  return !!n && n.depth < MAX_DEPTH;
}

/** SortOrder for a new sibling: last + 1 among the parent's children (roots when parentId is blank). */
export function nextSortOrder(tree, parentId = '') {
  const siblings = parentId ? (tree?.byId?.get(parentId)?.children || []) : (tree?.roots || []);
  return Math.max(0, ...siblings.map(n => Number(n.sortOrder) || 0)) + 1;
}

/** The first palette hue no root uses yet; cycles when every hue is taken. */
export function nextPaletteColor(tree) {
  const used = new Set((tree?.roots || []).map(r => (r.color || '').toLowerCase()));
  return PROJECT_PALETTE.find(c => !used.has(c.toLowerCase())) || PROJECT_PALETTE[(tree?.roots || []).length % PROJECT_PALETTE.length];
}

/** The tree with one node renamed / added — pure, for optimistic state. */
export function withProject(list, project) {
  const rest = (list || []).filter(p => p.id !== project.id);
  return [...rest, project];
}
export function withoutProject(list, id) {
  return (list || []).filter(p => p.id !== id);
}
